import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, map, Observable, shareReplay, tap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MainHeaderComponent } from '../../shared/main-header/main-header.component';
import { UserCardComponent } from '../../shared/user-card/user-card.component';
import { AuthService } from '../../../services/auth.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { APPDB } from '../../../recongition-system-api/db';
import { DashboardService } from '../board.service';
import { BoardParams, ClientOption, DashboardItem, DashboardType, RenderStrategy, UserData } from '../board.types';
import { BOARD_CLIENT_OPTIONS } from './board-list.clients';

type DashboardCardView = {
  name: string;
  urlBi: string;
  id: string;
  client_id: number;
  queries: unknown[];
  url_image: string;
  type: string;
  id_meta: number;
  // Tipo normalizado y decision de render, resueltos una sola vez al cargar la
  // card. `type` se conserva crudo solo para diagnostico.
  dashboardType: DashboardType | null;
  render: RenderStrategy;
  // Solo los parametros del usuario logueado, no el mapa completo por uid.
  userParams: BoardParams;
  // Claves de `parameters_objects` que este usuario no tiene. Si no esta vacio,
  // el tablero no se le muestra: no podria consultar sus queries.
  missingParams: string[];
  parameters_objects?: Record<string, Record<string, string>>;
};

@Component({
  selector: 'app-board-list',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule, MatButtonModule,
            MatTooltipModule, MainHeaderComponent, UserCardComponent],
  templateUrl: './board-list.component.html',
  styleUrl: './board-list.component.scss'
})
export class BoardListComponent {
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly analytics = inject(AnalyticsService);
  private readonly db = inject(APPDB);

  dashBoards$!: Observable<DashboardCardView[]>;
  filteredDashboards$!: Observable<DashboardCardView[]>;
  user$!: Observable<UserData>;
  clients$!: Observable<ClientOption[]>;

  // Franja de acento sólida por tablero: paleta única (sin duplicados),
  // en tonos morados de marca ordenados de más oscuro a más claro.
  private readonly transparency = 1;
  private readonly baseColors: string[] = [
    '#4B0082', '#483D8B', '#6A5ACD', '#7B68EE', '#9370DB',
    '#8A2BE2', '#9400D3', '#9932CC', '#BA55D3', '#8B008B',
    '#800080',
  ];

  colors: string[] = [];
  selectedClientValue = 'all';
  isNavigating = false;
  private selectedClient = new BehaviorSubject<string>('all');
  private searchTerm = new BehaviorSubject<string>('');

  ngOnInit(): void {
    const initialClientId = this.route.snapshot.queryParamMap.get('client_id');
    if (initialClientId) {
      this.selectedClient.next(initialClientId);
      this.selectedClientValue = initialClientId;
    }

    this.user$ = this.auth.userData$.pipe(
      filter((d): d is UserData => !!d)
    );

    // shareReplay: una sola llamada a getDashboards() (y por lo tanto un solo
    // listener de Firestore) compartida entre dashBoards$, clients$ y
    // filteredDashboards$, en vez de que cada `| async` dispare su propia
    // suscripción/consulta a Firestore.
    this.dashBoards$ = this.dashboardService.getDashboards().pipe(
      map((dashboards: DashboardItem[]) => {
        const uid = this.dashboardService.currentUid;
        return dashboards.map((dashboard): DashboardCardView => {
          const dashboardType = this.dashboardService.resolveDashboardType(dashboard.type);
          return {
            name: dashboard.name,
            urlBi: dashboard.urlBi,
            id: dashboard.id,
            client_id: dashboard.client_id,
            queries: dashboard.queries,
            url_image: dashboard.url_image,
            type: dashboard.type,
            id_meta: dashboard.dashboard,
            dashboardType,
            render: this.dashboardService.resolveRenderStrategy(dashboardType),
            // Proyeccion al uid del usuario: de aqui en adelante nadie puede
            // tomar por error los parametros de otro usuario.
            userParams: this.dashboardService.getParamsForUser(dashboard.parameters, uid),
            missingParams: this.dashboardService.typeUsesParams(dashboardType)
              ? this.dashboardService.getMissingParams(dashboard, uid)
              : [],
            parameters_objects: dashboard.parameters_objects
          };
        });
      }),
      // tap((views) => this.logUserParams(views)),   // diagnostico de parametros
      // Regla: sin los parametros que el tablero declara en `parameters_objects`,
      // el usuario no puede consultarlo — se excluye del listado en vez de
      // dejarlo entrar a una pantalla de error.
      map((views) => views.filter((v) => v.missingParams.length === 0)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.clients$ = this.dashBoards$.pipe(
      map((dashboards) => {
        const uniqueIds = [...new Set(dashboards.map((d) => d.client_id))];
        return uniqueIds.map((id) => {
          const foundClient = BOARD_CLIENT_OPTIONS.find((client) => client.id === id);
          return foundClient || { id, name: `Cliente ${id}` };
        });
      })
    );

    this.filteredDashboards$ = combineLatest([
      this.dashBoards$,
      this.selectedClient.asObservable(),
      this.searchTerm.asObservable().pipe(debounceTime(150), distinctUntilChanged()),
    ]).pipe(
      map(([dashboards, selectedId, search]) => {
        const byClient = selectedId === 'all'
          ? dashboards
          : dashboards.filter((d) => String(d.client_id) === selectedId);

        const normalizedSearch = this.normalize(search);
        if (!normalizedSearch) return byClient;

        return byClient.filter((d) => this.normalize(d.name).includes(normalizedSearch));
      })
    );

    this.buildColorPalette();
  }

  onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.next(value);
  }

  hasActiveFilters(): boolean {
    return this.selectedClient.getValue() !== 'all' || this.searchTerm.getValue().trim().length > 0;
  }

  clearFilters(): void {
    this.selectedClient.next('all');
    this.selectedClientValue = 'all';
    this.searchTerm.next('');
    if (this.searchInputRef) {
      this.searchInputRef.nativeElement.value = '';
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { client_id: null },
      queryParamsHandling: 'merge',
    });
  }

  onClientChange(clientId: string | number): void {
    const normalizedClientId = String(clientId);
    this.selectedClient.next(normalizedClientId);
    this.selectedClientValue = normalizedClientId;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { client_id: normalizedClientId === 'all' ? null : normalizedClientId },
      queryParamsHandling: 'merge',
    });
  }

  onCardClick(dashboard: DashboardCardView, user: UserData): void {
    if (dashboard.type === 'metabase') {
      this.fetchAndOpenMetabase(dashboard, user);
    } else {
      this.openStandardDashboard(dashboard);
    }
  }

  getBackgroundColor(index: number): string {
    return this.colors[index % this.colors.length];
  }

  // Diagnostico de parametros por usuario. Desactivado: expone en consola el uid
  // y los valores asignados. Para reactivarlo, descomentar este metodo y la linea
  // `tap(...)` del pipe de dashBoards$ en ngOnInit.
  //
  // private logUserParams(views: DashboardCardView[]): void {
  //   const uid = this.dashboardService.currentUid;
  //
  //   console.log(`[params] uid del usuario: ${uid ?? 'NULL'}`);
  //   if (!uid) {
  //     console.error(
  //       '[params] uid NULL: la consulta salio como where("roles.undefined","==","reader"), ' +
  //       'Firestore devuelve 0 documentos sin error y todos los parametros quedan vacios.'
  //     );
  //   }
  //
  //   console.table(views.map((v) => ({
  //     tablero: v.name,
  //     tipo: v.dashboardType ?? `— sin reconocer (type="${v.type}") —`,
  //     params_del_usuario: JSON.stringify(v.userParams),
  //     params_exigidos: Object.keys(v.parameters_objects ?? {}).join(', ') || '—',
  //     oculto: v.missingParams.length ? `SI · falta: ${v.missingParams.join(', ')}` : ''
  //   })));
  // }

  async openStandardDashboard(dashboard: DashboardCardView): Promise<void> {
    if (this.isNavigating) return;
    this.isNavigating = true;
    try {
      this.analytics.trackDashboardClick(dashboard.id, String(dashboard.client_id), dashboard.name);
      await this.db.saveDashboard(dashboard.id, dashboard.type, dashboard.urlBi);
      await this.router.navigate(['/boards', dashboard.id], {
        queryParams: this.getCurrentClientQueryParams(),
      });
    } catch (error) {
      console.error('Error opening dashboard:', error);
    } finally {
      this.isNavigating = false;
    }
  }

  async fetchAndOpenMetabase(dashboard: DashboardCardView, user: UserData): Promise<void> {
    if (this.isNavigating) return;
    this.isNavigating = true;
    try {
      const response = await firstValueFrom(
        this.dashboardService.enbedUrl(dashboard.userParams, dashboard.id_meta)
      );
      if (!response?.url) throw new Error('Invalid URL from server');
      await this.db.saveDashboard(dashboard.id, dashboard.type, response.url);
      await this.router.navigate(['/boards', dashboard.id], {
        queryParams: this.getCurrentClientQueryParams(),
      });
      this.analytics.trackDashboardClick(dashboard.id, String(dashboard.client_id), dashboard.name);
    } catch (error) {
      console.error('Error fetching Metabase URL:', error);
    } finally {
      this.isNavigating = false;
    }
  }

  private buildColorPalette(): void {
    this.colors = this.baseColors.map((color) => this.addTransparency(color, this.transparency));
  }

  private addTransparency(color: string, transparency: number): string {
    const [r, g, b] = this.hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${transparency})`;
  }

  private hexToRgb(hex: string): [number, number, number] {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
  }

  private normalize(value: string): string {
    return (value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
  }

  private getCurrentClientQueryParams(): { client_id?: string } {
    const selectedClientId = this.selectedClient.getValue();
    return selectedClientId === 'all' ? {} : { client_id: selectedClientId };
  }
}
